import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Switch,
  FormControlLabel,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import TablePaginationActions from '@material-ui/core/TablePagination/TablePaginationActions';
import { Alert } from '@mui/material';

import { ChapterRequestStatusEnum, useChapterRequestsQuery } from 'generated/graphql';
import { TableBox } from 'retired/Box';
import { selectOrganization, selectUser } from 'entities/user/selectors';

import AddNewChapterBlock from '../AddNewChapterBlock';
import ChapterRequestModal from '../ChapterRequestModal';

import ChapterRequestRow from './ChapterRequestRow';

const PendingChapterRequests = ({
  newChapterModalOpen,
  openNewChapterModal,
}: {
  newChapterModalOpen: boolean;
  openNewChapterModal: (isOpen: boolean) => void;
}) => {
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);

  const [page, setPage] = useState(0);
  const [limit, setRowsPerPage] = useState(10);

  const [selectedChapterRequest, setSelectedChapterRequest] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [viewAllChapterRequests, setViewAllChapterRequests] = useState(false);

  const { data, loading, error, refetch } = useChapterRequestsQuery({
    variables: {
      statuses: [ChapterRequestStatusEnum.Pending, ChapterRequestStatusEnum.Accepted],
      parentNonprofitId: viewAllChapterRequests ? undefined : organization?.nonprofitId,
      offset: page * limit,
      limit,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const handleModalOpenChange = useCallback(() => {
    if (!newChapterModalOpen) {
      refetch();
    }
  }, [newChapterModalOpen, refetch]);

  useEffect(() => {
    handleModalOpenChange();
  }, [handleModalOpenChange]);

  if (!user) {
    return null;
  }

  const selectChapterRequest = (chapterRequest) => {
    setSelectedChapterRequest(chapterRequest);
    setOpenEditModal(true);
  };

  const handleModalClose = () => {
    setOpenEditModal(false);
    setSelectedChapterRequest(null);
    refetch();
  };

  const { isAdmin } = user;

  const actionsHeader = isAdmin ? 'Review' : 'Edit / Revoke';

  const colSpan = isAdmin ? 7 : 6;

  const chapterRequests = data?.chapterRequests?.results || [];
  const total = data?.chapterRequests?.total || 0;

  if (error) {
    return <Alert security="error">There was an error loading the data</Alert>;
  }

  return (
    <>
      {loading ? <LinearProgress /> : <div style={{ height: 4 }} />}

      {(chapterRequests.length > 0 || isAdmin) && !loading && (
        <TableBox>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={colSpan} style={{ textAlign: 'right', borderBottom: 0 }}>
                  {isAdmin && (
                    <FormControlLabel
                      style={{ marginLeft: 24 }}
                      control={
                        <Switch
                          checked={viewAllChapterRequests}
                          onChange={(): void => setViewAllChapterRequests(!viewAllChapterRequests)}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label="View all chapter requests"
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                {isAdmin && <TableCell>Parent</TableCell>}
                <TableCell>Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Submitted on</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Admin Email</TableCell>
                <TableCell>{actionsHeader}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chapterRequests.map((chapterRequest) => (
                <ChapterRequestRow
                  key={chapterRequest.id}
                  chapterRequest={chapterRequest}
                  selectChapterRequest={selectChapterRequest}
                  refetchChapterRequests={() => {
                    refetch();
                  }}
                />
              ))}
            </TableBody>

            <TableFooter style={{ width: '100%' }}>
              <TableRow>
                <TableCell>{`Total: ${total}`}</TableCell>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  colSpan={colSpan - 1}
                  count={total}
                  rowsPerPage={limit}
                  page={page}
                  onPageChange={(_event, newPage): void => setPage(newPage)}
                  onRowsPerPageChange={(event): void => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableBox>
      )}

      {chapterRequests.length === 0 && !loading && !isAdmin && (
        <AddNewChapterBlock setOpen={openNewChapterModal} />
      )}

      {openEditModal && (
        <ChapterRequestModal
          modalOpen={openEditModal}
          chapterRequest={selectedChapterRequest}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default PendingChapterRequests;
